import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Label } from "@sablier/v2-components/molecules";
import { Input } from "@sablier/v2-components/organisms";
import { OTHER_TOKEN_LISTS } from "@sablier/v2-constants";
import { useDebounce, useRequestTokenList } from "@sablier/v2-hooks";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Token } from "@sablier/v2-models";
import AutoSizer from "react-virtualized-auto-sizer";
import { FixedSizeList as List } from "react-window";
import { useToken } from "wagmi";
import {
  useChain,
  useTokenSearch,
  useTokens,
  useTokensLocal,
} from "~/client/hooks";
import type { IToken, IWagmiAddress } from "@sablier/v2-types";
import { Chip, Empty, Item, Loader } from "../common";
import { ItemAction } from "../common/Item";

const WrapperPartial = styled.div`
  flex: 1;
  flex-shrink: 0;
  width: 100%;
`;

const Content = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex-shrink: 0;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
    height: 100%;
    padding-inline: ${(props) => props.theme.sizes.modalEdge};
    padding-top: ${(props) => props.theme.sizes.edge};
    padding-bottom: 0;
  }
`;

const Sources = styled.div`
  ${(props) => props.theme.styles.row}
  & {
    flex-wrap: wrap;
    gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    width: 100%;
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1);
    border-bottom: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Search = styled.div`
  width: 100%;
`;

const Results = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    flex: 1;
    row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
    width: 100%;
  }
`;

const ListWrapper = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    position: relative;
    width: 100%;
    height: 100%;
    padding-top: 4px;
    border-top: 2px solid ${(props) => props.theme.colors.border};
  }
`;

const Single = styled.div`
  width: 100%;
  height: 60px;
`;

const ChipDivider = styled.div`
  width: 2px;
  height: 42px;
  background-color: ${(props) => props.theme.colors.dark400};
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxMD} {
    ${Content} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
      padding: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    }
  }
  ${(props) => props.theme.medias.maxSM} {
    ${Content} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      padding: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    }
    ${Search} {
      margin-bottom: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    }
  }
`;

interface ItemData {
  action: ItemAction;
  list: IToken[];
  onClick: (item: IToken) => void;
}

interface RegistryProps {
  search?: string;
  list: IToken[];
  onSave: (item: IToken) => void;
}

function RegistryFromChain({ onSave, search }: RegistryProps) {
  const { id: chainId } = useChain();
  const isEnabled = useMemo(() => _.isEthereumAddress(search, true), [search]);
  const { t } = useT();
  const address = useMemo(() => {
    if (isEnabled) {
      return _.toAddress(search) as IWagmiAddress;
    }
    return undefined;
  }, [isEnabled, search]);

  const { data, isError, isLoading } = useToken({
    address,
    chainId,
    enabled: isEnabled,
  });
  const count = useMemo(() => (!data || isError ? 0 : 1), [data, isError]);
  const { list: raw } = useTokens();

  const onItemClick = useCallback(
    (item: IToken) => {
      onSave(item);
    },
    [onSave],
  );

  const itemData: ItemData = useMemo(() => {
    let item = raw?.find(
      (item) =>
        search &&
        _.toAddress(search) &&
        _.toAddress(item.address) === _.toAddress(search),
    );
    if (!item && chainId && !isError && !isLoading && !!data) {
      item = Token.fromUniswapToken({ ...data, chainId });
    }

    return {
      action: ItemAction.ACCEPT,
      list: item ? [item] : [],
      onClick: onItemClick,
    };
  }, [chainId, data, isError, onItemClick, isLoading, raw, search]);

  return (
    <Results>
      <Label
        value={`${_.capitalize(t("words.results"))}${
          count ? ` (${count})` : ""
        }`}
      />
      <ListWrapper>
        {count === 0 && !isLoading && <Empty content={t("structs.noToken")} />}
        {isLoading ? (
          <Content>
            <Loader />
          </Content>
        ) : (
          !isError &&
          !!data &&
          isEnabled && (
            <Single>
              <Item data={itemData} index={0} />
            </Single>
          )
        )}
      </ListWrapper>
    </Results>
  );
}

function RegistryFromList({
  isLoading,
  list,
  onSave,
  search,
}: RegistryProps & {
  isLoading: boolean;
}) {
  const { t } = useT();
  const { id: chainId } = useChain();
  const virtualListRef = useRef<HTMLDivElement>();
  const debounced = useDebounce(search, 100);
  const sorted = useTokenSearch(list, debounced);
  const available = useMemo(
    () => sorted.filter((t) => t.chainId === chainId),
    [chainId, sorted],
  );

  const labelResult = list.length
    ? ` (${available.length}/${list.length})`
    : "";
  const labelEmpty = !available.length
    ? t("structs.nothingAvailable")
    : undefined;

  const onItemClick = useCallback(
    (item: IToken) => {
      onSave(item);
    },
    [onSave],
  );

  const itemData: ItemData = useMemo(
    () => ({
      action: ItemAction.SAVE,
      list: available,
      onClick: onItemClick,
    }),
    [available, onItemClick],
  );

  useEffect(() => {
    if (virtualListRef?.current) {
      virtualListRef.current.scrollTop = 0;
    }
  }, [debounced]);

  return (
    <Results>
      <Label
        value={`${_.capitalize(t("words.results"))}${labelResult}`}
        tooltip={{
          value: t("structs.filteredByChain"),
        }}
      />
      <ListWrapper>
        {available.length === 0 && !isLoading && <Empty content={labelEmpty} />}
        {isLoading ? (
          <Content>
            <Loader />
          </Content>
        ) : (
          <AutoSizer>
            {({ height, width }: { width: number; height: number }) => (
              <List<ItemData>
                height={height ?? 0}
                itemCount={available.length}
                itemData={itemData}
                itemSize={60}
                outerRef={virtualListRef}
                width={width ?? 0}
              >
                {Item}
              </List>
            )}
          </AutoSizer>
        )}
      </ListWrapper>
    </Results>
  );
}
interface Props {
  onHome: () => void;
}
function TokenPickerRegistry({}: Props) {
  const { t } = useT();
  const [search, setSearch] = useState<string>();
  const [source, setSource] = useState<
    (typeof OTHER_TOKEN_LISTS)[0] | undefined
  >();

  const { chain } = useChain();
  const { addLocal } = useTokensLocal();
  const { isLoading: isLoadingRaw, list: raw } = useTokens();
  const { isLoading: isLoadingCustom, list: custom } = useRequestTokenList({
    url: source?.url,
  });

  const isLoading = useMemo(
    () => isLoadingCustom || isLoadingRaw,
    [isLoadingCustom, isLoadingRaw],
  );

  const filtered = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return custom.map((c) => {
      const r = raw.find((r) => Token.areEqual(r, c));
      if (r) {
        return r;
      }
      return c;
    });
  }, [custom, isLoading, raw]);

  const onSourceChoose = useCallback(
    (value: typeof source) => {
      setSource(value);
    },
    [setSource],
  );

  const onItemSave = useCallback(
    (token: IToken) => {
      addLocal(token);
    },
    [addLocal],
  );

  return (
    <Wrapper>
      <Content>
        <Sources>
          <Chip
            title={t("modalCards.token.chipTitle")}
            image={chain?.image}
            isActive={!source}
            onClick={() => onSourceChoose(undefined)}
          />
          <ChipDivider />
          {OTHER_TOKEN_LISTS.map((item) => (
            <Chip
              key={item.url}
              title={`List: ${item.title}`}
              image={item.icon}
              isActive={source && source.url === item.url}
              onClick={() => onSourceChoose(item)}
            />
          ))}
        </Sources>
        <Search>
          <Input.TextSearch
            id={"tokenPickerSearch"}
            onChange={(value) => {
              setSearch(value ?? "");
            }}
            onClear={() => {
              setSearch("");
            }}
            placeholder={
              source
                ? t("form.placeholder.textSearch")
                : t("form.placeholder.addressSearch")
            }
            value={search}
          />
        </Search>
        {source ? (
          <RegistryFromList
            isLoading={isLoading}
            list={filtered}
            onSave={onItemSave}
            search={search}
          />
        ) : (
          <RegistryFromChain
            list={filtered}
            onSave={onItemSave}
            search={search}
          />
        )}
      </Content>
    </Wrapper>
  );
}

export default TokenPickerRegistry;
